import React, { useCallback, useEffect, useRef, useState } from "react";
import { View } from "react-native";
import { Hide, mq } from "react-native-unistyles";

import Button from "$src/core/components/atoms/Button/Button";
import DatePicker from "$src/core/components/molecules/DatePicker/DatePicker";
import EPGChannelGrid, {
  type EPGChannelGridHandle,
} from "$src/core/components/organisms/EPGChannelGrid";
import EPGChannelSelector from "$src/core/components/organisms/EPGChannelSelector";
import EPG_DAY_RANGE_DEFAULTS from "$src/core/constants/epg-day-range";
import useConfig from "$src/core/hooks/use-config";
import { useEpgDayModel } from "$src/core/hooks/use-epg-day-model";
import useTabBarHeight from "$src/core/hooks/use-tab-bar-height";
import useTranslation from "$src/core/hooks/use-translation";
import ErrorPage from "$src/core/pages/ErrorPage/ErrorPage";
import LoadingPage from "$src/core/pages/LoadingPage/LoadingPage";
import MessagePage from "$src/core/pages/MessagePage/MessagePage";
import useEPGConfigQuery from "$src/core/queries/use-epg-config-query";
import useEPGQuery from "$src/core/queries/use-epg-query";
import $pageInsets from "$src/core/styles/page";
import getAppTimeZone from "$src/core/utils/date/get-app-time-zone";
import resolveEpgTimeZone from "$src/core/utils/epg/resolve-epg-time-zone";

import $ from "./EPGPage.web.styles";

const EPGPage = () => {
  const { t } = useTranslation();
  const config = useConfig();
  const tabBarHeight = useTabBarHeight();
  const channelGridRef = useRef<EPGChannelGridHandle | null>(null);
  const [isDateChanging, setIsDateChanging] = useState(false);

  const { data: epgConfig } = useEPGConfigQuery();
  const timeZone = resolveEpgTimeZone(epgConfig, getAppTimeZone(config.intl));
  const {
    data: epg,
    status,
    error,
    refetch,
  } = useEPGQuery({
    epgConfig,
    timeZone,
  });

  const {
    daysAfter,
    daysBefore,
    dayBounds,
    dayChannels,
    selectedChannelId,
    selectedChannelIndex,
    selectedDateOffset,
    setSelectedDateOffset,
    setUserSelectedChannelId,
    timelineScrollAnchor,
  } = useEpgDayModel({
    epg,
    epgConfig,
    defaults: EPG_DAY_RANGE_DEFAULTS,
    timeZone,
  });

  const canScrollPrev = selectedChannelIndex > 0;
  const canScrollNext = selectedChannelIndex < dayChannels.length - 1;
  $.useVariants({ canScrollPrev, canScrollNext });

  const handleDateSelect = useCallback(
    (offset: number) => {
      // A press on the selected day would otherwise remount the grid and throw
      // away every scroll position for no change.
      if (offset === selectedDateOffset) {
        return;
      }

      setIsDateChanging(true);
      setSelectedDateOffset(offset);
    },
    [selectedDateOffset, setIsDateChanging, setSelectedDateOffset],
  );

  const handleMovePreviousChannel = useCallback(() => {
    if (!dayChannels.length || !canScrollPrev) return;

    const prevIndex = Math.max(0, selectedChannelIndex - 1);
    const prevChannelId = dayChannels[prevIndex]?.id;
    if (!prevChannelId) return;

    setUserSelectedChannelId(prevChannelId);
    channelGridRef.current?.scrollToChannelIndex(prevIndex);
  }, [
    canScrollPrev,
    dayChannels,
    selectedChannelIndex,
    setUserSelectedChannelId,
  ]);

  const handleMoveNextChannel = useCallback(() => {
    if (!dayChannels.length || !canScrollNext) return;

    const nextIndex = Math.min(
      dayChannels.length - 1,
      selectedChannelIndex + 1,
    );
    const nextChannelId = dayChannels[nextIndex]?.id;
    if (!nextChannelId) return;

    setUserSelectedChannelId(nextChannelId);
    channelGridRef.current?.scrollToChannelIndex(nextIndex);
  }, [
    canScrollNext,
    dayChannels,
    selectedChannelIndex,
    setUserSelectedChannelId,
  ]);

  useEffect(() => {
    if (!isDateChanging) {
      return undefined;
    }

    const id = requestAnimationFrame(() => {
      setIsDateChanging(false);
    });
    return () => cancelAnimationFrame(id);
  }, [isDateChanging, selectedDateOffset]);

  if (status === "pending") {
    return <LoadingPage />;
  }

  if (status === "error") {
    return (
      <ErrorPage
        title={t("ERROR/EPG_PAGE/TITLE")}
        subtitle={t("ERROR/EPG_PAGE/SUBTITLE")}
        ctaText={t("COMMON/RETRY")}
        ctaIcon="retry"
        error={error}
        onPressCta={refetch}
      />
    );
  }

  return (
    <View style={[$pageInsets.vertical({ tabBarHeight }), $.container]}>
      <View style={$.controls}>
        <DatePicker
          daysAfter={daysAfter}
          daysBefore={daysBefore}
          timeZone={timeZone}
          selectedOffset={selectedDateOffset}
          onDateSelect={handleDateSelect}
        />
      </View>
      {!dayChannels.length || !dayBounds ? (
        <MessagePage
          icon="folder"
          title={t("EPG_PAGE/EMPTY_TITLE")}
          subtitle={t("EPG_PAGE/EMPTY_SUBTITLE")}
        />
      ) : (
        <>
          <View style={$.channelControls}>
            <EPGChannelSelector
              channels={dayChannels}
              onChannelSelect={setUserSelectedChannelId}
              selectedChannelId={selectedChannelId}
            />
          </View>
          <View style={$.grid}>
            <Hide mq={mq.only.width(undefined, "lg")}>
              <View style={[$.overlay, $.previousOverlay]}>
                <Button
                  name="epg_previous_channel"
                  preset="secondary"
                  icon="chevron-left"
                  accessibilityLabel={t("COMMON/PREVIOUS/CTA/LABEL")}
                  onPress={handleMovePreviousChannel}
                  style={$.navButton}
                />
              </View>
            </Hide>

            <Hide mq={mq.only.width(undefined, "lg")}>
              <View style={[$.overlay, $.nextOverlay]}>
                <Button
                  name="epg_next_channel"
                  accessibilityLabel={t("COMMON/NEXT/CTA/LABEL")}
                  preset="secondary"
                  icon="chevron-right"
                  onPress={handleMoveNextChannel}
                  style={$.navButton}
                />
              </View>
            </Hide>

            {isDateChanging ? (
              <LoadingPage />
            ) : (
              <EPGChannelGrid
                ref={channelGridRef}
                channels={dayChannels}
                highlightLivePrograms={selectedDateOffset === 0}
                onVisibleChannelChange={setUserSelectedChannelId}
                selectedChannelIndex={Math.max(0, selectedChannelIndex)}
                timelineScrollAnchor={timelineScrollAnchor}
              />
            )}
          </View>
        </>
      )}
    </View>
  );
};

export default EPGPage;
